<template>
  <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

    <!-- Card Header -->
    <div class="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-orange-500 to-orange-700
                flex items-center justify-between">
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
        <span class="text-white font-semibold text-sm">All Images</span>
        <span class="bg-white/20 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
          {{ galleries.total ?? 0 }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Select
          v-model="perPage"
          :options="perPageOptions"
          optionLabel="label"
          optionValue="value"
          @change="getGalleries(null)"
          class="!text-sm !rounded-lg !bg-white/15 !border-white/30 !text-white !w-20"
        />
        <button @click="getGalleries(null)"
                class="p-1.5 bg-white/15 hover:bg-white/25 rounded-lg transition-colors" title="Refresh">
          <svg class="w-4 h-4 text-white" :class="{ 'animate-spin': galleryLoading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-slate-50 border-b border-slate-100">
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">ID</th>
            <th class="px-5 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Image</th>
            <th class="px-5 py-3.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>

        <!-- Skeleton -->
        <tbody v-if="galleryLoading">
          <tr v-for="i in 5" :key="i" class="border-b border-slate-50">
            <td class="px-5 py-4"><div class="h-4 w-8 bg-slate-200 rounded-lg animate-pulse"></div></td>
            <td class="px-5 py-4"><div class="h-20 w-20 bg-slate-200 rounded-xl animate-pulse"></div></td>
            <td class="px-5 py-4 text-center"><div class="h-8 w-24 bg-slate-200 rounded-xl animate-pulse mx-auto"></div></td>
          </tr>
        </tbody>

        <!-- Empty state -->
        <tbody v-else-if="!galleries.data || galleries.data.length === 0">
          <tr>
            <td colspan="3" class="px-5 py-20">
              <div class="text-center">
                <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg class="w-8 h-8 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>
                <p class="font-semibold text-slate-600">No images yet</p>
                <p class="text-sm text-slate-400 mt-1">Upload your first gallery image to get started.</p>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- Data Rows -->
        <tbody v-else class="divide-y divide-slate-50">
          <tr v-for="image in galleries.data" :key="image.id"
              class="hover:bg-orange-50/40 transition-colors duration-150 group">

            <!-- ID -->
            <td class="px-5 py-4">
              <span class="font-mono text-xs font-bold text-orange-700 bg-orange-50 px-2 py-1 rounded-lg tracking-wider">
                #{{ image.id }}
              </span>
            </td>

            <!-- Image -->
            <td class="px-5 py-4">
              <div class="relative w-20 h-20 group/img">
                <img
                  :src="image.url"
                  alt="Gallery Image"
                  class="w-20 h-20 object-cover rounded-xl border-2 border-slate-100 shadow-sm
                         group-hover/img:shadow-md group-hover/img:scale-105 group-hover/img:border-orange-200
                         transition-all duration-300"
                />
              </div>
            </td>

            <!-- Actions -->
            <td class="px-5 py-4 text-center">
              <div class="flex items-center justify-center gap-1">
                <RouterLink :to="{ name: 'app.galleries.edit', params: { id: image.id } }"
                            class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                                   bg-orange-50 text-orange-700 border border-orange-100
                                   hover:bg-orange-100 hover:border-orange-200
                                   rounded-xl transition-all duration-200 group-hover:shadow-sm">
                  <PencilIcon class="w-3.5 h-3.5"/>
                  <span class="hidden sm:inline">Edit</span>
                </RouterLink>
                <button @click="deleteGallery(image.id)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold
                               bg-red-50 text-red-600 border border-red-100
                               hover:bg-red-100 hover:border-red-200
                               rounded-xl transition-all duration-200">
                  <TrashIcon class="w-3.5 h-3.5"/>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="!galleryLoading && galleries.total > 0"
         class="px-6 py-4 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-3">
      <span class="text-sm text-slate-500">
        Showing
        <span class="font-semibold text-slate-700">{{ galleries.from }}</span>
        –
        <span class="font-semibold text-slate-700">{{ galleries.to }}</span>
        of
        <span class="font-semibold text-orange-600">{{ galleries.total }}</span>
        images
      </span>
      <nav v-if="galleries.total > galleries.limit"
           class="inline-flex rounded-xl shadow-sm -space-x-px overflow-hidden border border-slate-200">
        <a v-for="(link, i) of galleries.links" :key="i"
           :disabled="!link.url" href="#"
           @click.prevent="getForPage($event, link)"
           class="relative inline-flex items-center px-3.5 py-2 border-e border-slate-200 text-sm font-medium transition-all duration-150"
           :class="[
             link.active ? 'bg-orange-600 text-white z-10' : 'bg-white text-slate-600 hover:bg-orange-50 hover:text-orange-600',
             !link.url ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
           ]"
           v-html="link.label">
        </a>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import store from "@/store";
import { PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { RouterLink } from "vue-router";
import { Select } from "primevue";
import { useToast } from "primevue/usetoast";

const toast = useToast();
const perPage = ref(10);
const perPageOptions = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
];


const galleries = computed(() => store.state.galleries);
const galleryLoading = ref(true);

function getGalleries(url = null) {
  galleryLoading.value = true;
  store.dispatch("getGalleries", { url, perPage: perPage.value })
    .then(() => { galleryLoading.value = false; });
}

function getForPage(ev, link) {
  if (!link.url || link.active) return;
  getGalleries(link.url);
}

function deleteGallery(id) {
  if (!confirm("Are you sure you want to delete this image?")) return;
  store.dispatch("deleteGallery", id).then(() => {
    toast.add({ severity: "success", summary: "Deleted", detail: "Image deleted successfully", life: 3000 });
    getGalleries();
  });
}

onMounted(() => { getGalleries(); });
</script>
